import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-cyber-dark/95 backdrop-blur-xl"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, rotateX: -10 }}
          animate={{ scale: 1, opacity: 1, rotateX: 0 }}
          exit={{ scale: 0.8, opacity: 0, rotateX: 10 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-cyber-black/90 border border-cyber-teal/50 p-8 max-w-lg w-full mx-4 relative shadow-cyber backdrop-blur-md"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, rgba(10, 14, 26, 0.95), rgba(20, 27, 45, 0.95))',
          }}
        >
          {/* Corner Brackets */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyber-teal"></div>
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-cyber-teal"></div>
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-cyber-teal"></div>
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyber-teal"></div>

          {/* Close Button */}
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            className="absolute top-4 right-4 text-cyber-gray hover:text-cyber-teal text-2xl font-mono"
          >
            ×
          </motion.button>

          {/* Header */}
          <div className="border-b border-cyber-teal/30 pb-6 mb-8">
            <motion.h2 
              className="font-orbitron text-cyber-aqua text-2xl font-bold mb-3 glitch-text animate-pulse-neon"
              data-text="IDENTITY_MODULE"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              IDENTITY_MODULE
            </motion.h2>
            <motion.div 
              className="text-xs text-cyber-gray font-mono"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              [LOADING NEURAL_PROFILE... COMPLETE]
            </motion.div>
          </div>

          {/* Profile Image */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="relative">
              <img 
                src="/bashexo2.png" 
                alt="BasH Profile" 
                className="w-24 h-24 rounded-full border-2 border-cyber-teal/50 shadow-cyber"
              />
              <div className="absolute inset-0 rounded-full bg-cyber-teal/10 animate-pulse"></div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div 
            className="space-y-5 text-sm font-mono"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-cyber-gray uppercase text-xs mb-1">IDENTITY:</div>
                <div className="text-cyber-teal">Muhamad Bashirusdan bin Azemi</div>
              </div>
              <div>
                <div className="text-cyber-gray uppercase text-xs mb-1">CALLSIGN:</div>
                <div className="text-cyber-aqua font-orbitron font-bold">BasH</div>
              </div>
            </div>

            <div>
              <div className="text-cyber-gray uppercase text-xs mb-1">ORIGIN_COORDS:</div>
              <div className="text-cyber-teal">Kelantan, Malaysia</div>
            </div>

            <div>
              <div className="text-cyber-gray uppercase text-xs mb-1">PRIMARY_ROLE:</div>
              <div className="text-cyber-green">Medical Officer [Surgical Division]</div>
            </div>

            <div>
              <div className="text-cyber-gray uppercase text-xs mb-1">SECONDARY_MODULES:</div>
              <div className="text-cyber-purple">
                Developer · Drone Pilot · Visual Architect
              </div>
            </div>

            <div>
              <div className="text-cyber-gray uppercase text-xs mb-1">TECH_ARSENAL:</div>
              <div className="text-cyber-blue text-xs">
                Go • PHP • React • Laravel • FPV • DaVinci Resolve
              </div>
            </div>

            <div>
              <div className="text-cyber-gray uppercase text-xs mb-1">COMM_CHANNEL:</div>
              <div className="text-cyber-yellow break-all">
                muhamadbashirushdan@gmail.com
              </div>
            </div>

            <div className="border-t border-cyber-teal/30 pt-5 mt-8">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-cyber-green rounded-full animate-pulse"></div>
                  <span className="text-cyber-green font-bold">
                    STATUS: AVAILABLE_FOR_COLLABORATION
                  </span>
                </div>
                <div className="text-cyber-gray">
                  v3.0.1
                </div>
              </div>
            </div>
          </motion.div>

          {/* Holographic overlay effect */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="hologram w-full h-full opacity-30"></div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AboutModal;